/* eslint-disable react-refresh/only-export-components */
// Authentication, backed by Logto. Components use useAuth() and never the SDK directly, so the
// identity provider sits behind one file.
//
// The sign-in callback comes back to `${origin}${callbackPath}` (/callback for the dashboard,
// /join/callback for the invitation flow). After the callback is handled we return to the page
// the user started from, saved in sessionStorage before the redirect.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { LogtoProvider, UserScope, useHandleSignInCallback, useLogto, type LogtoConfig } from "@logto/react";
import { getRuntimeConfig } from "./runtimeConfig";

export type AuthUser = {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
};

type AuthState = {
  user?: AuthUser;
  isAuthenticated: boolean;
  isLoading: boolean;
  error?: Error;
  login: () => Promise<void>;
  logout: (returnTo?: string) => Promise<void>;
  /** Access token for our API. `refresh` drops the cached one first (e.g. after joining an org). */
  getAccessToken: (opts?: { refresh?: boolean }) => Promise<string>;
};

const AuthContext = createContext<AuthState | null>(null);
const RETURN_TO = "fj:returnTo";
const SIGN_IN_REDIRECT_AT = "fj:signInRedirectAt";
const SIGN_IN_GUARD_MS = 5 * 60 * 1000;

const isCallbackUrl = (callbackPath: string) =>
  window.location.pathname === callbackPath && new URLSearchParams(window.location.search).has("code");

// Unreadable storage counts as recent, so a broken setup shows the error instead of bouncing to Logto.
const signInStartedRecently = () => {
  try {
    return Date.now() - Number(sessionStorage.getItem(SIGN_IN_REDIRECT_AT)) < SIGN_IN_GUARD_MS;
  } catch {
    return true;
  }
};

const AuthState = ({ callbackPath, children }: { callbackPath: string; children: ReactNode }) => {
  const logto = useLogto();
  const { isAuthenticated, isLoading, error, getIdTokenClaims, signIn, signOut, clearAccessToken } = logto;
  const [user, setUser] = useState<AuthUser | undefined>();
  const [claimsLoaded, setClaimsLoaded] = useState(false);
  const [onCallback] = useState(() => isCallbackUrl(callbackPath));
  // The SDK's isLoading counts every call (getAccessToken, getIdTokenClaims, ...), not just start-up.
  // Only start-up should hold the app: following the counter unmounted the whole tree on every API call,
  // and the claims effect below, which itself bumps the counter, ran in a loop and locked the tab.
  const [started, setStarted] = useState(false);

  useHandleSignInCallback(() => {
    const returnTo = sessionStorage.getItem(RETURN_TO) || "/";
    sessionStorage.removeItem(RETURN_TO);
    window.location.replace(returnTo);
  });

  useEffect(() => {
    if (!isLoading) setStarted(true);
  }, [isLoading]);

  useEffect(() => {
    if (!started) return;
    if (!isAuthenticated) {
      setUser(undefined);
      setClaimsLoaded(true);
      return;
    }
    let cancelled = false;
    getIdTokenClaims().then((c) => {
      if (cancelled) return;
      setUser(
        c && {
          sub: c.sub,
          email: c.email ?? undefined,
          email_verified: c.email_verified,
          name: c.name ?? undefined,
          picture: c.picture ?? undefined,
        },
      );
      setClaimsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [started, isAuthenticated, getIdTokenClaims]);

  const login = useCallback(async () => {
    sessionStorage.setItem(RETURN_TO, window.location.pathname + window.location.search);
    await signIn(`${window.location.origin}${callbackPath}`);
  }, [signIn, callbackPath]);

  // A session that stopped working (e.g. an expired refresh token) leaves the tokens in place and the
  // SDK never clears `error`, so every load ended on "Login failed". signIn clears the tokens, so we
  // sign in again instead: once per error, and not again within five minutes, to avoid a redirect loop.
  // The decision is made in render, so the error page is never shown on the way out.
  const [retriedError, setRetriedError] = useState<Error>();
  const sessionLost = !!error && isAuthenticated && !onCallback;
  const retrySignIn = sessionLost && (error === retriedError || !signInStartedRecently());

  useEffect(() => {
    if (!retrySignIn || error === retriedError) return;
    try {
      sessionStorage.setItem(SIGN_IN_REDIRECT_AT, String(Date.now()));
    } catch {
      // Unwritable storage reads as recent next time, which stops the loop.
    }
    setRetriedError(error);
    void login();
  }, [retrySignIn, error, retriedError, login]);

  const logout = useCallback(
    async (returnTo?: string) => {
      await signOut(returnTo);
    },
    [signOut],
  );

  const getAccessToken = useCallback(
    async (opts?: { refresh?: boolean }) => {
      if (opts?.refresh) await clearAccessToken();
      const token = await logto.getAccessToken(getRuntimeConfig().apiResource);
      if (!token) throw new Error("no access token");
      return token;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [logto.getAccessToken, clearAccessToken],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      isAuthenticated,
      // Still loading while the SDK starts, while we read the claims, and for as long as we sit on the
      // callback URL: the SDK checks for the callback asynchronously, and treating that moment as
      // "logged out" would start a second sign-in.
      isLoading: retrySignIn || !started || !claimsLoaded || (onCallback && !error),
      error: retrySignIn ? undefined : error,
      login,
      logout,
      getAccessToken,
    }),
    [user, isAuthenticated, started, claimsLoaded, onCallback, error, retrySignIn, login, logout, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

let logtoConfig: LogtoConfig | null = null;
const getLogtoConfig = (): LogtoConfig => {
  if (!logtoConfig) {
    const rc = getRuntimeConfig();
    logtoConfig = {
      endpoint: rc.logtoEndpoint,
      appId: rc.logtoAppId,
      resources: [rc.apiResource],
      scopes: [UserScope.Email, UserScope.Profile],
    };
  }
  return logtoConfig;
};

export const AuthProvider = ({ callbackPath, children }: { callbackPath: string; children: ReactNode }) => (
  <LogtoProvider config={getLogtoConfig()}>
    <AuthState callbackPath={callbackPath}>{children}</AuthState>
  </LogtoProvider>
);

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};

/** Null outside <AuthProvider>, as on the public card and respond pages. */
export const useOptionalAuth = (): AuthState | null => useContext(AuthContext);
